/* Document-level base styles + the installer for the global sheets.

   Only genuinely global rules belong here — everything else is scoped inside a
   component's shadow root. Custom properties from tokens.ts do the crossing. */

import { css as litCss } from 'lit';
import { tokensCss } from './tokens.js';

const css = String.raw;

/**
 * Reset for use INSIDE a shadow root. Every component must put this first in
 * its `static styles`.
 *
 * Document-level stylesheets (including adoptedStyleSheets) do not cross shadow
 * boundaries, and `box-sizing` is not an inherited property — so the global
 * reset below reaches the document tree only. Without this, a component that
 * declares `width: var(--rail-w)` silently renders wider by its own
 * padding and border. Custom properties DO inherit through shadow roots, which
 * is why tokens need no equivalent.
 */
export const shadowReset = litCss`
  :host,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  /* Kill double-tap-to-zoom on anything tappable, without touching pinch.

     'manipulation' means "panning and pinch-zoom only" — the double-tap gesture
     is dropped, so the browser stops holding every tap for ~300ms to see whether
     a second one is coming. Pinch survives, deliberately: it is the accessibility
     escape hatch, and the phone grid leans on it now that the tiles carry no
     title (ADR 0009, Library pass). Disabling zoom outright would also be
     ignored by Safari, so it would not even be consistent.

     Here rather than in the document sheet because that sheet cannot cross a
     shadow boundary, and every interactive element in this app lives inside one.
     A component that needs the raw stream — map-canvas — overrides with
     'touch-action: none' locally. */
  button,
  a[href],
  input,
  select,
  textarea,
  label,
  [role='button'],
  [role='tab'],
  [role='switch'] {
    touch-action: manipulation;
  }
`;

/**
 * A cluster of icon-only buttons acting on the thing they sit in — a card, a list
 * row, a field. Apply to an element with class `acts`; that class name is the
 * contract.
 *
 * Shared because this was independently re-derived three times and drifted every
 * time: two icon sizes (15px/16px), two hit-area paddings (3px/4px), and one copy
 * that omitted the touch fallback in `revealOnHover` entirely. A shadow root gets
 * no styling from its siblings, so "the same CSS" has to be an explicit import.
 *
 * --icon-size is declared here rather than inline on each <g-icon> because custom
 * properties inherit *through* shadow boundaries — one declaration reaches every
 * icon in the cluster.
 */
export const iconActions = litCss`
  .acts {
    display: flex;
    flex: none;
    gap: 2px;
    color: var(--text-dim);
    --icon-size: 15px;
    --g-button-pad: 4px;
  }
  /* On touch the boxes themselves grow, rather than getting an invisible hit
     area like isolated buttons do (see \`touchTarget\`). These clusters are the
     one place where expanding hit areas past the paint would be actively
     dangerous: a place row's Visited and Remove sit 2px apart, so overlapping
     targets would mean tapping "visited" and getting "remove". Real spacing is the only
     honest fix. 20px icon + 12px padding either side ≈ 46px. */
  @media (pointer: coarse) {
    .acts {
      gap: var(--space-1);
      --icon-size: 20px;
      --g-button-pad: var(--space-3);
    }
  }
`;

/**
 * A bar of equal segments split by hairlines: Settings' theme control and its
 * category tray, and Map's detail actions.
 *
 * Moved here from `g-settings/row.ts` on acquiring its second workspace — the
 * same trip `iconActions` made, and for the same reason: a shadow root inherits
 * nothing from its siblings, so sharing has to be an explicit import or it is
 * three drifted copies.
 *
 * Two columns by default; a caller with a different count overrides
 * `grid-template-columns` and nothing else. The inset margin is the Settings
 * case (always on screen inside a card); callers that sit flush override
 * `.split`'s margin, as the category tray and `place-detail` both do.
 *
 * Quiet at rest (§31); `del` tints `--danger` only under the finger, the touch
 * analogue of g-button's danger hover.
 */
export const splitBar = litCss`
  .split {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: var(--space-2) var(--row-pad-r) var(--space-2)
      var(--row-pad-l);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    overflow: hidden;
  }
  .act {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    min-height: var(--touch-min);
    padding: var(--space-2) var(--space-3);
    border: 0;
    background: none;
    color: var(--text-dim);
    font: inherit;
    font-family: var(--font-ui);
    font-size: 0.82rem;
    cursor: pointer;
    transition: background-color 120ms ease, color 120ms ease;
    --icon-size: 18px;
  }
  .act + .act {
    border-left: 1px solid var(--border);
  }
  .act:active {
    background: var(--surface-2);
  }
  .act.del:active {
    color: var(--danger);
    background: color-mix(in oklab, var(--danger) 12%, transparent);
  }
  .act:focus-visible {
    outline: none;
    box-shadow: var(--ring);
  }
  @media (hover: hover) {
    .act:hover {
      background: var(--surface-2);
    }
    .act.del:hover {
      color: var(--danger);
      background: color-mix(in oklab, var(--danger) 12%, transparent);
    }
  }
`;

/**
 * Suppress the browser's own long-press menu over app imagery.
 *
 * Found on a real device: long-pressing a Library tile opens Chrome's image
 * context menu — "Copy image / Download image / Share image / Open in Chrome
 * browser" — over a grid that is meant to read as an app surface. The reel
 * thumbnail is chrome, not a document image, and offering to download it is
 * both wrong and a dead end.
 *
 * Two mechanisms because no single one covers both platforms: the CSS callout
 * property is iOS-only, and Android needs the contextmenu event refused. The
 * event half is the caller's job — see `suppressCallout` — because a stylesheet
 * cannot cancel an event.
 *
 * Deliberately NOT global. Long-press over text is how selection and copy work,
 * and Detail's transcript is text a reader may legitimately want to lift.
 */
export const noCallout = litCss`
  :host,
  img {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }
  img {
    /* Long-press-to-drag is the other half of the same affordance on Android. */
    -webkit-user-drag: none;
  }
`;

/**
 * The event half of `noCallout`, for a component's own listener.
 *
 * Gated on a coarse pointer so a right-click on a desktop still gets the normal
 * browser menu — suppressing that would be hostile, and the problem it solves
 * only exists for a thumb.
 */
export function suppressCallout(e: Event): void {
  if (window.matchMedia('(pointer: coarse)').matches) e.preventDefault();
}

/**
 * Give a button a thumb-sized hit area without changing how big it looks.
 *
 * For components with their own shadow root, applied to the inner `button` —
 * outside CSS cannot reach it, so this has to be imported, not inherited.
 *
 * **Vertical only, deliberately.** Expanding width too would make a chip
 * narrower than 44px overlap its neighbour, and an invisible target that
 * straddles two controls is a worse bug than a small one: you cannot see why the
 * wrong thing fired. Rows are the axis where neighbours are already far apart,
 * so height is where the expansion is free.
 */
export const touchTarget = litCss`
  @media (pointer: coarse) {
    button {
      position: relative;
    }
    button::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 50%;
      translate: 0 -50%;
      height: 100%;
      min-height: var(--touch-min);
    }
  }
`;

/**
 * §19 — secondary actions appear on hover. Layers on top of `iconActions`.
 *
 * Two things here are load-bearing rather than stylistic:
 *
 * - `opacity`, not `display`/`visibility`: the buttons stay in the tab order
 *   while invisible, so archive/delete remain reachable without a pointer.
 * - the `hover: none` fallback is not optional. On a touch device nothing ever
 *   hovers, so without it these actions can never be revealed at all.
 *
 * Keyed off `:host` so it fits both shapes in use: a component that *is* the row
 * (settings-category-row) and one that wraps an inner container (index-card).
 */
export const revealOnHover = litCss`
  .acts {
    opacity: 0;
    transition: opacity 120ms ease;
  }
  :host(:hover) .acts,
  :host(:focus-within) .acts {
    opacity: 1;
  }
  @media (hover: none) {
    .acts {
      opacity: 1;
    }
  }
`;

const BASE = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html,
  body {
    height: 100%;
    /* Kill the browser's own tap flash — the translucent blue-grey box a phone
       paints over whatever it decides you pressed. It is the wrong shape often
       enough to be noise: it takes the element's border-box, so it squares off
       a rounded button, and on a row whose visual extent is a background it
       lights up the whole strip a beat before our own press state does.

       Here, in the document sheet, rather than in shadowReset: unlike
       box-sizing this property IS inherited, so it crosses every shadow
       boundary the same way the tokens do. One declaration reaches the app.

       This removes the ONLY press feedback some controls had, so anything
       tappable needs its own :active state — see the ones added alongside this
       (g-button, index-row, place-row, item-row, item-tile, g-tabbar). A new
       tappable surface owes one too; without it a tap looks like nothing
       happened until the next view renders. */
    -webkit-tap-highlight-color: transparent;
  }

  /* iOS standalone is the one place height:100% is short.

     ADR 0010 §5 chose 100% over 100dvh on Android, where dvh overhangs the
     visible box by the nav bar and hides the tab bar. iOS installed fails the
     opposite way: the document is anchored at y=0 under the translucent status
     bar (index.html asks for black-translucent) but 100% resolves to the height
     WITHOUT the top inset, so the shell ends short of the screen and the tab bar
     floats above a dead band — measured off an iPhone 14 screenshot as 47px, its
     safe-area-inset-top exactly.

     dvh rather than calc(100% + var(--safe-t)): that arithmetic fits the one
     device this was seen on, and encodes a guess about WHY. dvh asks for the
     viewport instead, and in standalone there are no collapsing toolbars, so
     dvh == lvh == svh — not the moving target ADR 0010 rejected for a browser
     tab.

     Gated twice, and both gates carry weight. -webkit-touch-callout is
     WebKit-only, so Chrome Android never sees this and ADR 0010's measurement
     (dvh 775 against a 719 visible box) cannot be undone by it. display-mode
     keeps an iOS Safari TAB on 100%, where dvh really does grow and shrink with
     the toolbars. Desktop Safari matches the first gate and not the second. */
  @supports (-webkit-touch-callout: none) {
    @media (display-mode: standalone) {
      html,
      body {
        height: 100dvh;
      }
    }
  }

  body {
    margin: 0;
    background: var(--bg);
    color: var(--text);
    font-family: var(--font-ui);
    font-size: 15px;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    /* PWA: the shell paints under the notch / Dynamic Island. Individual chrome
       adds env(safe-area-inset-*) padding in the deferred mobile pass. */
    padding: 0;
  }

  /* the shell owns all scrolling; the document itself must not scroll */
  body {
    overflow: hidden;
  }

  :focus-visible {
    outline: none;
    box-shadow: var(--ring);
    border-radius: var(--radius-sm);
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

/** Adopt tokens + base into the document once, at bootstrap. */
export function installGlobalStyles(): void {
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(tokensCss + BASE);
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
}
